// RegDefence AI — Claude client.
// Adapted verbatim from CCL-AGENT-APP/src/lib/api.js (proven in production).

const API_URL = '/api/claude';
const TOKEN_KEY = 'regdefence.authToken';

// ─── Auth token helpers ─────────────────────────────────────────────────
// The "token" is the shared app password. Stored in sessionStorage so it
// clears when the tab closes; never persisted to localStorage.

export function getAuthToken() {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAuthToken(token) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(TOKEN_KEY, token);
  } catch {}
}

export function clearAuthToken() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(TOKEN_KEY);
  } catch {}
}

/** Build Authorization header (or empty object) from current session token. */
export function authHeader() {
  const t = getAuthToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** Verify a candidate password against /api/check-auth. */
export async function verifyPassword(password) {
  const res = await fetch('/api/check-auth', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${password}` },
  });
  if (res.status === 200) return { ok: true };
  let error = `HTTP ${res.status}`;
  try {
    const body = await res.json();
    error = body.error || error;
  } catch {}
  return { ok: false, error, status: res.status };
}

/**
 * Call a RegDefence agent via the secure proxy.
 * @param {string} agent - 'fact-extractor' | 'kyc-qa' | 'red-flag' | 'defensibility' | 'scorer' | 'reporter'
 * @param {Array<{role: 'user'|'assistant', content: string}>} messages
 * @param {{model?: string, max_tokens?: number, system?: string}} options
 * @returns {Promise<{agent: string, model: string, content: any[], usage: object, stop_reason: string}>}
 */
export async function callAgent(agent, messages, options = {}) {
  const response = await fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({
      agent,
      messages,
      system: options.system || undefined,
      model: options.model || 'claude-sonnet-4-20250514',
      max_tokens: options.max_tokens || 2048,
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Unknown error' }));
    if (response.status === 401) {
      clearAuthToken();
      const e = new Error(err.error || 'Session expired — please re-enter the password');
      e.code = 'UNAUTHORIZED';
      throw e;
    }
    throw new Error(err.detail || err.error || `API error ${response.status}`);
  }

  return response.json();
}

/**
 * Extract concatenated text from a Claude API response.
 * @param {{content: Array<{type: string, text?: string}>}} response
 * @returns {string}
 */
export function extractText(response) {
  if (!response?.content) return '';
  return response.content
    .filter((b) => b.type === 'text')
    .map((b) => b.text)
    .join('\n');
}

/**
 * Parse a CAPS-headed Claude response into structured sections.
 * Recognises lines like "EXECUTIVE SUMMARY" or "AREA NAME:" as section breaks.
 * @param {string} text
 * @returns {{sections: Array<{heading: string, content: string}>, raw: string}}
 */
export function parseAgentResponse(text) {
  const sectionPattern = /^([A-Z][A-Z 0-9/&,()-]+):?\s*$/gm;
  const sections = [];
  let lastIndex = 0;
  let lastHeading = null;
  let match;

  while ((match = sectionPattern.exec(text)) !== null) {
    if (lastHeading !== null) {
      sections.push({
        heading: lastHeading,
        content: text.slice(lastIndex, match.index).trim(),
      });
    }
    lastHeading = match[1].trim();
    lastIndex = match.index + match[0].length;
  }
  if (lastHeading !== null) {
    sections.push({
      heading: lastHeading,
      content: text.slice(lastIndex).trim(),
    });
  }

  return { sections, raw: text };
}

/**
 * Try to parse a JSON object from a Claude response. Tolerates code-fenced
 * output and leading/trailing prose.
 * @param {string} text
 * @returns {any|null}
 */
export function parseJsonResponse(text) {
  if (!text) return null;
  // Strip markdown code fences if present.
  const stripped = text
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/```\s*$/i, '')
    .trim();
  // Find the outermost { ... } block.
  const start = stripped.indexOf('{');
  const end = stripped.lastIndexOf('}');
  if (start === -1 || end === -1 || end < start) return null;
  try {
    return JSON.parse(stripped.slice(start, end + 1));
  } catch {
    return null;
  }
}

/**
 * Call an agent expected to return JSON. If the first attempt fails to parse,
 * send one corrective turn back to the same agent with the bad output and a
 * "return ONLY valid JSON" instruction. Throws after the second failure so
 * the orchestrator can surface a real error instead of silently degrading.
 *
 * @param {string} agent
 * @param {Array<{role:'user'|'assistant', content:string}>} messages
 * @param {{model?: string, max_tokens?: number, system?: string}} options
 * @returns {Promise<{ data: any, raw: string, attempts: number }>}
 */
export async function callAgentJson(agent, messages, options = {}) {
  // Helper to merge usage from the corrective second call (if it happens) so
  // cost telemetry includes both attempts.
  const sumUsage = (a, b) => ({
    input_tokens: (a?.input_tokens || 0) + (b?.input_tokens || 0),
    output_tokens: (a?.output_tokens || 0) + (b?.output_tokens || 0),
  });

  const first = await callAgent(agent, messages, options);
  const firstText = extractText(first);
  const firstParse = parseJsonResponse(firstText);
  if (firstParse !== null) {
    return {
      data: firstParse, raw: firstText, attempts: 1,
      model: first.model, usage: first.usage,
    };
  }

  // Corrective re-prompt: feed the bad output back as an assistant turn,
  // then ask for valid JSON. Same model, same system prompt, same context.
  const correction = [
    ...messages,
    { role: 'assistant', content: firstText },
    {
      role: 'user',
      content:
        'Your previous response was not valid JSON. Output ONLY the JSON object specified in your instructions. No prose, no code fence, no preamble. Start with `{` and end with `}`.',
    },
  ];
  const second = await callAgent(agent, correction, options);
  const secondText = extractText(second);
  const secondParse = parseJsonResponse(secondText);
  if (secondParse !== null) {
    return {
      data: secondParse, raw: secondText, attempts: 2,
      model: second.model || first.model,
      usage: sumUsage(first.usage, second.usage),
    };
  }

  const err = new Error(
    `Agent "${agent}" failed to return valid JSON after 2 attempts. ` +
      `First 200 chars of last response: ${secondText.slice(0, 200)}`
  );
  err.agent = agent;
  err.lastRaw = secondText;
  throw err;
}
